import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const Section = styled.section`
  min-height: 100vh;
  padding: 4rem 2rem;
  background: #000000ff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Card = styled(motion.div)`
  width: 100%;
  max-width: 1100px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 245, 255, 0.15);
  border-radius: 32px;
  padding: 3rem;
  backdrop-filter: blur(16px);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; height: 4px;
    background: linear-gradient(90deg, #0072ff, #00f5ff);
  }

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    padding: 2rem;
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Title = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  background: linear-gradient(135deg, #bbe2f9ff, #000000ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1.5rem;
`;

const InputBox = styled.div`
  background: rgba(0, 0, 0, 0.4);
  border: 1px dashed rgba(0, 245, 255, 0.4);
  border-radius: 20px;
  padding: 2rem;
  font-size: 1.6rem;
  color: white;
  font-weight: 100;
  opacity: 0.75;
  text-align: center;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #00f5ff;
  font-weight: 600;
  font-size: 1.1rem;
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Step = styled(motion.div)`
  background: rgba(0, 245, 255, 0.06);
  border-left: 3px solid #00f5ff;
  padding: 1.2rem 1.5rem;
  border-radius: 0 16px 16px 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -9px;
    top: 1.5rem;
    width: 12px;
    height: 12px;
    background: #00f5ff;
    border-radius: 50%;
    box-shadow: 0 0 20px #00f5ff;
  }
`;

const StepTitle = styled.div`
  font-weight: 700;
  color: white;
  margin-bottom: 0.4rem;
`;

const StepDesc = styled.div`
  font-size: 0.95rem;
  color: #cccccc;
  line-height: 1.5;
`;

const Research = styled(motion.div)`
  background: rgba(0, 245, 255, 0.08);
  border: 1px solid rgba(0, 245, 255, 0.25);
  border-radius: 20px;
  padding: 1.5rem;
  font-size: 0.95rem;
  color: #dddddd;
  line-height: 1.7;
`;

const SuccessEngine = () => {
  const { t } = useTranslation();

  return (
    <Section>
      <Card
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        {/* ЛЕВО: ТВОЯ ЦЕЛЬ */}
        <Left>
          <Title>{t('success_title')}</Title>
          <InputBox dangerouslySetInnerHTML={{ __html: t('success_input') }} />
        </Left>

        {/* ПРАВО: AI ДЕЙСТВУЕТ */}
        <Right>
          <Header>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M9 12l2 2 4-4" />
            </svg>
            {t('success_header')}
          </Header>

          <Timeline>
            <Step>
              <StepTitle>{t('success_step1_title')}</StepTitle>
              <StepDesc>{t('success_step1_desc')}</StepDesc>
            </Step>
            <Step>
              <StepTitle>{t('success_step2_title')}</StepTitle>
              <StepDesc>{t('success_step2_desc')}</StepDesc>
            </Step>
            <Step>
              <StepTitle>{t('success_step3_title')}</StepTitle>
              <StepDesc dangerouslySetInnerHTML={{ __html: t('success_step3_desc') }} />
            </Step>
            <Step>
              <StepTitle>{t('success_step4_title')}</StepTitle>
              <StepDesc dangerouslySetInnerHTML={{ __html: t('success_step4_desc') }} />
            </Step>
          </Timeline>

          <Research dangerouslySetInnerHTML={{ __html: t('success_research') }} />
        </Right>
      </Card>
    </Section>
  );
};

export default SuccessEngine;